import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../App';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { StorefrontLinkBanner } from '../components/provider/StorefrontLinkBanner';
import { StripeConnectBanner } from '../components/provider/StripeConnectBanner';
import { StatusBanner } from '../components/provider/StatusBanner';
import { LockedDashboardPreview } from '../components/provider/LockedDashboardPreview';
import { ServiceFormDialog } from '../components/provider/ServiceFormDialog';
import { OverviewTab } from '../components/provider/OverviewTab';
import { BookingsTab } from '../components/provider/BookingsTab';
import { ServicesTab } from '../components/provider/ServicesTab';
import { PortfolioTab } from '../components/provider/PortfolioTab';
import EarningsTab from './EarningsTab';
import { AccountingTab } from '../components/provider/AccountingTab';
import { ProductsTab } from '../components/provider/ProductsTab';
import { OrdersTab } from '../components/provider/OrdersTab';
import AvailabilityTab from './AvailabilityTab';
import DiscountsTab from './DiscountsTab';
import PoliciesTab from './PoliciesTab';
import ReviewsTab from './ReviewsTab';
import AnalyticsTab from './AnalyticsTab';
import TransformationsTab from './TransformationsTab';
import VideosTab from './VideosTab';
import RecognisedDevicesCard from '../components/account/RecognisedDevicesCard';
import { DeleteAccountCard } from '../components/account/DeleteAccountCard';
import TwoFactorSettingsCard from '../components/account/TwoFactorSettingsCard';
import TaxInfoCard from '../components/account/TaxInfoCard';
import TaxInfoBanner from '../components/account/TaxInfoBanner';
import EmergencyContactsCard from '../components/safety/EmergencyContactsCard';
import FeatureRequestsSection from '../components/feedback/FeatureRequestsSection';
import FoundingProviderBadge from '../components/provider/FoundingProviderBadge';
import { NotificationPreferencesCard } from '../components/account/NotificationPreferencesCard';
import { GettingStartedCard } from '../components/provider/GettingStartedCard';
import { PortfolioPhotoPrompt } from '../components/provider/PortfolioPhotoPrompt';
import { CoOpCreditsEmpty } from '../components/provider/CoOpCreditsEmpty';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useScrollActiveTabIntoView } from '../hooks/useScrollActiveTabIntoView';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Calendar, Package, Star, PoundSterling, 
  Eye, EyeOff, Tag, ShieldCheck, BarChart3, Layers, Video, Lock, FileText, Truck, Sparkles
} from 'lucide-react';

export default function ProviderDashboard() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const tabsScrollRef = useScrollActiveTabIntoView(activeTab);
  
  // Service form
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', duration: 60, price: '', category: 'hair_styling_care', service_types: [], image: '', deposit_amount: '', payment_type: 'full', co_op_tier: null,
  });
  const [editingService, setEditingService] = useState(null);
  
  // Portfolio state
  const [portfolio, setPortfolio] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [portfolioCaption, setPortfolioCaption] = useState('');
  const [portfolioCategory, setPortfolioCategory] = useState('');
  
  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ bio: '', brand_name: '', location: '', postcode: '', specialties: [] });
  const [uploadingProfileImg, setUploadingProfileImg] = useState(false);
  
  // Addons for inline assignment in service form
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [serviceFormErrors, setServiceFormErrors] = useState({});

  // Co-Op student programme: fetched once; the dialog hides its Co-Op
  // section entirely if the provider isn't student-verified.
  const [coOpStatus, setCoOpStatus] = useState(null);
  const [coOpTiers, setCoOpTiers] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [statusRes, tiersRes] = await Promise.all([
          api.get('/co-op/status'),
          api.get('/co-op/tiers'),
        ]);
        setCoOpStatus(statusRes.data);
        setCoOpTiers(tiersRes.data?.tiers || null);
      } catch {
        // Non-blocking: Co-Op section just stays hidden on error.
      }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [providerRes, bookingsRes] = await Promise.all([
        api.get('/providers/me'),
        api.get('/bookings')
      ]);
      
      setProvider(providerRes.data);
      setBookings(bookingsRes.data || []);
      
      if (providerRes.data?.provider_id) {
        const [servicesRes, portfolioRes, addonsRes] = await Promise.all([
          api.get(`/providers/${providerRes.data.provider_id}/services`),
          api.get('/providers/me/portfolio'),
          api.get('/addons').catch(() => ({ data: [] })),
        ]);
        setServices(servicesRes.data || []);
        setPortfolio(portfolioRes.data || []);
        setAvailableAddons(addonsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle subscription return from Stripe
    const params = new URLSearchParams(window.location.search);
    const subStatus = params.get('subscription');
    const sessionId = params.get('session_id');
    if (subStatus === 'success' && sessionId) {
      const confirmSub = async () => {
        try {
          const res = await api.post(`/subscriptions/confirm?session_id=${sessionId}`);
          if (res.data?.status === 'active') {
            toast.success('Welcome to Zuriya Pro!');
            setActiveTab('earnings');
          }
        } catch (err) {
          console.error('Subscription confirmation error:', err);
        }
        // Clean URL params
        window.history.replaceState({}, '', window.location.pathname);
      };
      confirmSub();
    } else if (subStatus === 'cancelled') {
      toast.info('Subscription upgrade cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Re-verify session on dashboard mount (protects against stale auth)
    const verifyAndFetch = async () => {
      try {
        await api.get('/auth/me');
        fetchData();
      } catch (err) {
        console.error('Session verification failed:', err);
        navigate('/login', { replace: true });
      }
    };
    verifyAndFetch();
  }, [fetchData, navigate]);

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingProfileImg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/providers/me/profile-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProvider(prev => ({ ...prev, profile_image: res.data.url }));
      toast.success('Profile picture updated');
    } catch (err) { console.error('Profile image upload error:', err); toast.error('Failed to upload'); }
    finally { setUploadingProfileImg(false); }
  };

  const handleProfileUpdate = async () => {
    try {
      await api.put('/providers/me', profileForm);
      setProvider(prev => ({ ...prev, ...profileForm }));
      setEditingProfile(false);
      toast.success('Profile updated');
    } catch (err) { console.error('Profile update error:', err); toast.error('Failed to update profile'); }
  };

  const handleCreateService = async () => {
    // Up-front validation, give the provider a precise, visible reason rather
    // than letting the backend 422 silently or letting Pydantic return a vague
    // error. Each required field must match the ServiceCreate schema contract
    // (name, description, duration, price, category, service_types).
    const missing = [];
    if (!serviceForm.name?.trim()) missing.push('Service name');
    if (!serviceForm.description?.trim()) missing.push('Description');
    if (!serviceForm.duration || serviceForm.duration < 15) missing.push('Duration (at least 15 min)');
    const numericPrice = parseFloat(serviceForm.price);
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) missing.push('Price (greater than £0)');
    if (!serviceForm.category) missing.push('Category');
    if (!serviceForm.service_types || serviceForm.service_types.length === 0) {
      missing.push('At least one service type');
    }
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? `Please fill in: ${missing[0]}`
          : `Missing required fields: ${missing.join(', ')}`
      );
      // Broadcast so the dialog can highlight the exact empty fields.
      setServiceFormErrors(Object.fromEntries(missing.map((label, i) => [
        ['name', 'description', 'duration', 'price', 'category', 'service_types'][[
          'Service name', 'Description', 'Duration (at least 15 min)',
          'Price (greater than £0)', 'Category', 'At least one service type',
        ].indexOf(label)] || `field_${i}`,
        true,
      ])));
      return;
    }
    setServiceFormErrors({});
    try {
      // Service-core fields go through ServiceCreate/Update. Add-on linkage is
      // persisted via the dedicated PUT /services/{id}/addons endpoint, which
      // fans the change out to each add-on's `service_ids` array.
      const payload = { ...serviceForm };
      if (payload.deposit_amount === '' || payload.deposit_amount === null || payload.deposit_amount === undefined) {
        delete payload.deposit_amount;
      } else {
        payload.deposit_amount = parseFloat(payload.deposit_amount);
      }

      let targetServiceId;
      if (editingService) {
        await api.put(`/services/${editingService.service_id}`, payload);
        targetServiceId = editingService.service_id;
        toast.success('Service updated');
      } else {
        const res = await api.post('/services', payload);
        targetServiceId = res.data?.service_id;
        toast.success('Service created');
      }

      if (targetServiceId) {
        try {
          await api.put(`/services/${targetServiceId}/addons`, { addon_ids: selectedAddonIds });
        } catch (linkErr) {
          // Don't block the service save on a linkage failure, surface separately.
          toast.error(linkErr.response?.data?.detail || 'Service saved, but add-on linkage failed');
        }
      }

      setServiceDialogOpen(false);
      setServiceForm({ name: '', description: '', duration: 60, price: '', category: 'hair_styling_care', service_types: [], image: '', deposit_amount: '', payment_type: 'full' });
      setSelectedAddonIds([]);
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const handleServiceTypeToggle = (typeId) => {
    setServiceForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(typeId)
        ? prev.service_types.filter(t => t !== typeId)
        : [...prev.service_types, typeId]
    }));
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    if (status === 'refresh') {
      fetchData();
      return;
    }
    try {
      await api.put(`/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const editService = async (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      category: service.category,
      service_types: service.service_types || [],
      image: service.image || '',
      deposit_amount: service.deposit_amount ?? '',
      payment_type: service.payment_type || 'full',
    });
    // Derive the real set of currently-linked add-ons from the add-on side of
    // the relationship, the service document doesn't store this itself.
    try {
      const res = await api.get('/addons', { params: { service_id: service.service_id } });
      setSelectedAddonIds((res.data || []).map((a) => a.addon_id));
    } catch {
      setSelectedAddonIds([]);
    }
    setServiceDialogOpen(true);
  };

  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    if (portfolio.length >= 10) { toast.error('Maximum 10 portfolio images allowed'); return; }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('caption', portfolioCaption);
      formData.append('category', portfolioCategory);
      
      const res = await api.post('/providers/portfolio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPortfolio(prev => [res.data, ...prev]);
      setPortfolioCaption('');
      setPortfolioCategory('');
      toast.success('Image uploaded!');
      e.target.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePortfolioDelete = async (imageId) => {
    try {
      await api.delete(`/providers/portfolio/${imageId}`);
      setPortfolio(prev => prev.filter(img => img.image_id !== imageId));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  const isLocked = !provider || provider.status !== 'approved';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="provider-dashboard">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-medium text-[#1A1A1A]">Provider Dashboard</h1>
            <p className="text-stone-500">Welcome back, {provider?.brand_name || user?.name}</p>
          </div>
          {provider?.status === 'approved' && (
            <ServiceFormDialog
              open={serviceDialogOpen}
              onOpenChange={(open) => {
                setServiceDialogOpen(open);
                if (!open) setServiceFormErrors({});
              }}
              serviceForm={serviceForm}
              setServiceForm={setServiceForm}
              errors={serviceFormErrors}
              editingService={editingService}
              providerServiceTypes={provider?.service_types}
              availableAddons={availableAddons}
              selectedAddonIds={selectedAddonIds}
              setSelectedAddonIds={setSelectedAddonIds}
              onServiceTypeToggle={handleServiceTypeToggle}
              onSave={handleCreateService}
              coOpStatus={coOpStatus}
              coOpTiers={coOpTiers}
            />
          )}
        </div>

        {/* Status Banner */}
        {provider?.status !== 'approved' && <StatusBanner status={provider?.status} />}

        {/* Shareable Link Banner */}
        {provider?.status === 'approved' && provider?.username && (
          <StorefrontLinkBanner username={provider.username} />
        )}

        {/* Stripe Connect, must complete before payouts run */}
        <StripeConnectBanner
          providerStatus={provider?.status}
          onClick={() => setActiveTab('earnings')}
        />

        {/* Founding Provider Beta badge, auto-hides when BETA_MODE=false. */}
        <FoundingProviderBadge providerStatus={provider?.status} />

        {/* HMRC tax-info nudge (auto-hides when complete). */}
        {provider?.status === 'approved' && (
          <TaxInfoBanner onTapThrough={() => setActiveTab('security')} />
        )}
        {/* Locked/Blurred Preview for non-approved providers */}
        {isLocked && <LockedDashboardPreview status={provider?.status} />}

        {/* Dashboard Content (only for approved providers) */}
        {!isLocked && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div ref={tabsScrollRef} className="mb-8 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-hide">
              <TabsList className="inline-flex w-max bg-white border border-[#E6E4DD] h-auto p-1">
                <TabsTrigger value="overview" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">
                  <LayoutDashboard className="h-4 w-4 mr-2" />Overview
                </TabsTrigger>
                <TabsTrigger value="bookings" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white">
                  <Calendar className="h-4 w-4 mr-2" />Bookings
                </TabsTrigger>
                <TabsTrigger value="services" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="services-tab-trigger">
                  <Package className="h-4 w-4 mr-2" />Services
                </TabsTrigger>
                <TabsTrigger value="products" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="products-tab-trigger">
                  <Package className="h-4 w-4 mr-2" />Products
                </TabsTrigger>
                <TabsTrigger value="orders" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="orders-tab-trigger">
                  <Truck className="h-4 w-4 mr-2" />Orders
                </TabsTrigger>
                <TabsTrigger value="portfolio" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="portfolio-tab-trigger">
                  <Star className="h-4 w-4 mr-2" />Portfolio
                </TabsTrigger>
                <TabsTrigger value="transformations" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="transformations-tab-trigger">
                  <Layers className="h-4 w-4 mr-2" />Before & After
                </TabsTrigger>
                <TabsTrigger value="videos" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="videos-tab-trigger">
                  <Video className="h-4 w-4 mr-2" />Videos
                </TabsTrigger>
                <TabsTrigger value="earnings" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="earnings-tab-trigger">
                  <PoundSterling className="h-4 w-4 mr-2" />Earnings
                </TabsTrigger>
                <TabsTrigger value="accounting" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="accounting-tab-trigger">
                  <FileText className="h-4 w-4 mr-2" />Accounting
                </TabsTrigger>
                <TabsTrigger value="availability" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="availability-tab-trigger">
                  <Calendar className="h-4 w-4 mr-2" />Availability
                </TabsTrigger>
                <TabsTrigger value="discounts" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="discounts-tab-trigger">
                  <Tag className="h-4 w-4 mr-2" />Discounts
                </TabsTrigger>
                <TabsTrigger value="policies" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="policies-tab-trigger">
                  <ShieldCheck className="h-4 w-4 mr-2" />Policies
                </TabsTrigger>
                <TabsTrigger value="reviews" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="reviews-tab-trigger">
                  <Star className="h-4 w-4 mr-2" />Reviews
                </TabsTrigger>
                <TabsTrigger value="analytics" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="analytics-tab-trigger">
                  <BarChart3 className="h-4 w-4 mr-2" />Analytics
                </TabsTrigger>
                <TabsTrigger value="feedback" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="feedback-tab-trigger">
                  <Sparkles className="h-4 w-4 mr-2" />Feature requests
                </TabsTrigger>
                <TabsTrigger value="security" className="whitespace-nowrap shrink-0 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white" data-testid="security-tab-trigger">
                  <Lock className="h-4 w-4 mr-2" />Security
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview">
              <GettingStartedCard services={services} bookings={bookings} username={provider?.username} setActiveTab={setActiveTab} />
              {!loading && <PortfolioPhotoPrompt count={portfolio.length} setActiveTab={setActiveTab} />}
              <CoOpCreditsEmpty coOpStatus={coOpStatus} services={services} setActiveTab={setActiveTab} />
              <OverviewTab
                provider={provider}
                services={services}
                bookings={bookings}
                editingProfile={editingProfile}
                profileForm={profileForm}
                setProfileForm={setProfileForm}
                setEditingProfile={setEditingProfile}
                handleProfileUpdate={handleProfileUpdate}
                handleProfileImageUpload={handleProfileImageUpload}
                uploadingProfileImg={uploadingProfileImg}
                handleBookingStatus={handleBookingStatus}
                setActiveTab={setActiveTab}
                setProvider={setProvider}
              />
            </TabsContent>

            <TabsContent value="bookings" data-testid="bookings-tab">
              <BookingsTab bookings={bookings} handleBookingStatus={handleBookingStatus} username={provider?.username} />
            </TabsContent>

            <TabsContent value="services" data-testid="services-tab">
              <ServicesTab
                services={services}
                setServiceDialogOpen={setServiceDialogOpen}
                editService={editService}
                handleDeleteService={handleDeleteService}
              />
            </TabsContent>

            <TabsContent value="products" data-testid="products-tab">
              <ProductsTab provider={provider} />
            </TabsContent>

            <TabsContent value="orders" data-testid="orders-tab">
              <OrdersTab />
            </TabsContent>

            <TabsContent value="portfolio" data-testid="portfolio-tab">
              <PortfolioTab
                portfolio={portfolio}
                portfolioCaption={portfolioCaption}
                portfolioCategory={portfolioCategory}
                setPortfolioCaption={setPortfolioCaption}
                setPortfolioCategory={setPortfolioCategory}
                handlePortfolioUpload={handlePortfolioUpload}
                uploadingImage={uploadingImage}
                handlePortfolioDelete={handlePortfolioDelete}
              />
            </TabsContent>

            <TabsContent value="earnings" data-testid="earnings-tab-content">
              <EarningsTab />
            </TabsContent>

            <TabsContent value="accounting" data-testid="accounting-tab-content">
              <AccountingTab provider={provider} />
            </TabsContent>

            <TabsContent value="transformations" data-testid="transformations-tab-content">
              <TransformationsTab services={services} />
            </TabsContent>

            <TabsContent value="videos" data-testid="videos-tab-content">
              <VideosTab services={services} />
            </TabsContent>

            <TabsContent value="availability" data-testid="availability-tab-content">
              <AvailabilityTab />
            </TabsContent>

            <TabsContent value="discounts" data-testid="discounts-tab-content">
              <DiscountsTab username={provider?.username} brandName={provider?.brand_name} />
            </TabsContent>

            <TabsContent value="policies" data-testid="policies-tab-content">
              <PoliciesTab />
            </TabsContent>

            <TabsContent value="reviews" data-testid="reviews-tab-content">
              <ReviewsTab />
            </TabsContent>

            <TabsContent value="analytics" data-testid="analytics-tab-content">
              <AnalyticsTab />
            </TabsContent>

            <TabsContent value="feedback" data-testid="feedback-tab-content">
              <FeatureRequestsSection helperText="Tell us what would make running your business on Zuriya easier. Upvote what other providers have asked for, so we know what to build next." />
            </TabsContent>

            <TabsContent value="security" data-testid="security-tab-content">
              <div className="space-y-6">
                <TaxInfoCard />
                <TwoFactorSettingsCard />
                <EmergencyContactsCard />
                <NotificationPreferencesCard />
                <RecognisedDevicesCard />
                <DeleteAccountCard role="provider" />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
